import { getAuthInfo, serviceSync } from "../services/sync.service";
import { libMisc } from "./misc";

export class MediaQueueProcessor {
    private isProcessing = false;
    private onChangeResolve: (() => void) | undefined;

    queue(url: string) {
        (async () => {
            const mediaBlob = await serviceSync.db.mediaBlobs.get(url);
            if (mediaBlob) {
                await serviceSync.alertMediaUrlIsReady(url);
                return;
            }
            await serviceSync.db.mediaQueue.put({ url, failed: 0 });
            this.process();
        })();
    }

    async *waitForQueueToClear(): AsyncGenerator<{ progress: number; total: number }, void, undefined> {
        const total = await serviceSync.db.mediaQueue.count();
        if (total === 0) {
            return;
        }
        yield { progress: 0, total };
        while (true) {
            await new Promise<void>((resolve) => {
                this.onChangeResolve = resolve;
            });
            delete this.onChangeResolve;
            const newTotal = await serviceSync.db.mediaQueue.count();
            yield { progress: total - newTotal, total };
            if (newTotal === 0) {
                break;
            }
        }
    }

    process() {
        if (this.isProcessing) {
            return;
        }
        this.isProcessing = true;
        (async () => {
            const { apiBaseUrl } = getAuthInfo();
            while (true) {
                const toProcess = await serviceSync.db.mediaQueue.where("failed").equals(0).limit(3).toArray();
                if (toProcess.length > 0) {
                    const generators: (() => Promise<"success" | "unknown" | "failure">)[] = toProcess.map(({ url }) => async () => {
                        try {
                            // await libMisc.wait(500);
                            const response = await fetch(`${apiBaseUrl}${url}`);
                            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                            const blob = await response.blob();
                            const extension = libMisc.extractExtensionFromUrl(url);
                            const type = ["png", "jpg", "jpeg", "gif", "webp"].some(x => x === extension) ? "image" : "unknown";
                            if (type === "image") {
                                const aspectRatio = await libMisc.getImageAspectRatio(blob);
                                if (aspectRatio !== -1) {
                                    await serviceSync.db.mediaBlobs.put({
                                        url,
                                        blob,
                                        type,
                                        aspectRatio
                                    });
                                    return "success";
                                }
                            }
                            return "unknown";
                        } catch (err) {
                            console.error("Failed to fetch or parse blob:", err);
                            return "failure";
                        }
                    });
                    let queueHasShrunk = false;
                    const results = await Promise.all(generators.map(x => x()));
                    for (let i = 0; i < results.length; i++) {
                        const result = results[i];
                        const url = toProcess[i].url;
                        if (result === "success") {
                            await serviceSync.db.mediaQueue.delete(url);
                            queueHasShrunk = true;
                            await serviceSync.alertMediaUrlIsReady(url);
                        }
                        else if (result === "unknown") {
                            await serviceSync.db.mediaQueue.delete(url);
                            queueHasShrunk = true;
                        }
                        else if (result === "failure") {
                            await serviceSync.db.mediaQueue.put({
                                url,
                                failed: 1
                            });
                        }
                    }
                    if (queueHasShrunk) {
                        this.onChangeResolve?.();
                    }
                }
                else {
                    const failedEntries = await serviceSync.db.mediaQueue.where("failed").equals(1).toArray();
                    for (const { url } of failedEntries) {
                        await serviceSync.db.mediaQueue.put({ url, failed: 0 });
                    }
                    if (failedEntries.length > 0) {
                        await libMisc.wait(2000);
                    }
                    else {
                        break;
                    }
                }
            }
            this.isProcessing = false;
        })();
    }
}

